// FILE: /src/Components/Apps/AppsHistory.js

import React, { useEffect, useMemo, useState } from "react";
import { Button, Empty, Select, Spin, message } from "antd";
import { useNavigate } from "react-router-dom";
import { ArrowLeftOutlined, ReloadOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { fetchAppsCatalog, fetchAppsHistory } from "../../services/apps";
import AppHistoryItem from "./AppHistoryItem";

const { Option } = Select;

const AppsHistory = () => {
  const navigate = useNavigate();
  const { groups } = useSelector((state) => state.groups);

  const [loading, setLoading] = useState(true);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [apps, setApps] = useState([]);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 30,
    total: 0,
    pages: 0,
  });

  const [filters, setFilters] = useState({
    groupId: undefined,
    appId: undefined,
    status: undefined,
    page: 1,
    limit: 30,
  });

  const activeAndHistoricalApps = useMemo(() => {
    return apps.filter((app) => app.appId);
  }, [apps]);

  const loadCatalog = async () => {
    setCatalogLoading(true);

    try {
      const response = await fetchAppsCatalog();
      setApps(response.apps || []);
    } catch (error) {
      message.error(error.message || "Failed to load app list.");
    } finally {
      setCatalogLoading(false);
    }
  };

  const loadHistory = async (nextFilters = filters) => {
    setLoading(true);

    try {
      const response = await fetchAppsHistory(nextFilters);
      setItems(response.items || []);
      setPagination(
        response.pagination || {
          page: 1,
          limit: 30,
          total: 0,
          pages: 0,
        }
      );
    } catch (error) {
      message.error(error.message || "Failed to load Apps history.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCatalog();
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilter = (key, value) => {
    const nextFilters = {
      ...filters,
      [key]: value || undefined,
      page: 1,
    };

    setFilters(nextFilters);
    loadHistory(nextFilters);
  };

  const handlePageChange = (direction) => {
    const nextPage =
      direction === "next"
        ? Number(filters.page || 1) + 1
        : Number(filters.page || 1) - 1;

    const nextFilters = {
      ...filters,
      page: Math.max(nextPage, 1),
    };

    setFilters(nextFilters);
    loadHistory(nextFilters);
  };

  const handleOpenTransaction = (transaction) => {
    message.info(`Transaction ID: ${transaction._id}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/Dashboard/Apps")}>
          Back to Apps
        </Button>

        <Button icon={<ReloadOutlined />} onClick={() => loadHistory()}>
          Refresh
        </Button>
      </div>

      <div className="bg-white border rounded-3xl shadow-sm overflow-hidden">
        <div className="p-6 sm:p-8 border-b bg-gradient-to-br from-white to-blue-50">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-1">
            Apps History
          </h1>

          <p className="text-gray-500 max-w-2xl mb-0">
            Review recent app usage, credit charges, outputs, and failures. History automatically expires after 45 days.
          </p>
        </div>

        <div className="p-5 border-b bg-gray-50 flex flex-wrap gap-3">
          <Select
            allowClear
            placeholder="Filter by app"
            loading={catalogLoading}
            value={filters.appId}
            onChange={(value) => updateFilter("appId", value)}
            style={{ minWidth: 220 }}
          >
            {activeAndHistoricalApps.map((app) => (
              <Option key={app.appId} value={app.appId}>
                {app.name}
              </Option>
            ))}
          </Select>

          <Select
            allowClear
            placeholder="Filter by brand"
            value={filters.groupId}
            onChange={(value) => updateFilter("groupId", value)}
            style={{ minWidth: 220 }}
          >
            {(groups || []).map((group) => (
              <Option key={group._id} value={group._id}>
                {group.name}
              </Option>
            ))}
          </Select>

          <Select
            allowClear
            placeholder="Filter by status"
            value={filters.status}
            onChange={(value) => updateFilter("status", value)}
            style={{ minWidth: 180 }}
          >
            <Option value="processing">Processing</Option>
            <Option value="completed">Completed</Option>
            <Option value="failed">Failed</Option>
            <Option value="refunded">Refunded</Option>
          </Select>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="flex justify-center py-20">
              <Spin size="large" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-16">
              <Empty description="No app history found." />
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((transaction) => (
                <AppHistoryItem
                  key={transaction._id}
                  transaction={transaction}
                  groups={groups}
                  onOpenTransaction={handleOpenTransaction}
                />
              ))}
            </div>
          )}
        </div>

        {!loading && items.length > 0 && (
          <div className="p-5 border-t bg-gray-50 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Page {pagination.page} of {pagination.pages || 1} · {pagination.total} total
            </div>

            <div className="flex gap-2">
              <Button
                disabled={Number(filters.page || 1) <= 1}
                onClick={() => handlePageChange("prev")}
              >
                Previous
              </Button>

              <Button
                disabled={Number(filters.page || 1) >= Number(pagination.pages || 1)}
                onClick={() => handlePageChange("next")}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppsHistory;